import { type LoggerService, logger as defaultLogger } from '../LoggerService'
import { sanitizeUrl } from '../sanitize'
import {
  isAuthRefreshRequest,
  receiveRefreshDiagnostic,
  startRefreshDiagnostic
} from '../utils/authDiagnostics'

export type NetworkInterceptorOptions = {
  // URL prefixes for which successful (2xx) responses are also logged as
  // api.success. Everything else only logs api.error. Mirrors the old
  // per-instance axios `logSuccessful` flag, scoped by URL instead of by
  // instance since fetch/XHR calls aren't tied to a specific client.
  logSuccessfulUrlPrefixes?: string[]
  excludedUrlPrefixes?: string[]
  slowRequestThresholdMs?: number
}

const STATIC_ASSET_RE =
  /\.(js|mjs|css|map|png|jpe?g|gif|svg|webp|ico|woff2?|ttf|eot|otf)(\?.*)?$/i
const FEDERATION_CHUNK_RE = /remoteEntry\.js|mf-manifest\.json/i

function isStaticAssetUrl(url: string | undefined): boolean {
  if (!url) return false
  return STATIC_ASSET_RE.test(url) || FEDERATION_CHUNK_RE.test(url)
}

function normalizeUrl(url: string | undefined): string | undefined {
  return url ? sanitizeUrl(url) : undefined
}

function shouldLogSuccess(
  url: string | undefined,
  prefixes: string[]
): boolean {
  if (!url || prefixes.length === 0) return false
  return prefixes.some(prefix => prefix && url.startsWith(prefix))
}

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

function resolveFetchMethod(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): string {
  if (init?.method) return init.method.toUpperCase()
  if (typeof input === 'object' && 'method' in input) {
    return input.method.toUpperCase()
  }
  return 'GET'
}

function shouldExclude(url: string | undefined, prefixes: string[]): boolean {
  return !!url && prefixes.some(prefix => prefix && url.startsWith(prefix))
}

function correlationFromResponse(response: Response): {
  requestId?: string
  traceId?: string
} {
  const headers = response.headers
  if (!headers || typeof headers.get !== 'function') return {}
  return {
    requestId:
      headers.get('x-request-id') ?? headers.get('request-id') ?? undefined,
    traceId:
      headers.get('traceparent') ?? headers.get('x-trace-id') ?? undefined
  }
}

function patchFetch(
  logger: LoggerService,
  options: NetworkInterceptorOptions
): () => void {
  const origFetch = window.fetch
  const boundFetch = origFetch.bind(window)
  const prefixes = options.logSuccessfulUrlPrefixes ?? []
  const excludedPrefixes = options.excludedUrlPrefixes ?? []
  const slowThreshold = options.slowRequestThresholdMs ?? 5_000

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const url = resolveFetchUrl(input)
    if (isStaticAssetUrl(url) || shouldExclude(url, excludedPrefixes)) {
      return boundFetch(input, init)
    }

    const method = resolveFetchMethod(input, init)
    let attemptId: string | undefined
    if (isAuthRefreshRequest(url, method)) {
      let body: unknown = init?.body
      if (body == null && typeof input === 'object' && 'clone' in input) {
        try {
          body = await input.clone().text()
        } catch {
          // Leave the original request untouched when its body cannot be read.
        }
      }
      attemptId = startRefreshDiagnostic(logger, 'fetch', body)
    }
    const start = performance.now()

    try {
      const response = await boundFetch(input, init)
      const durationMs = Math.round(performance.now() - start)
      const normalizedUrl = normalizeUrl(url)
      const correlation = correlationFromResponse(response)
      if (attemptId) {
        let body: unknown
        try {
          body = await response.clone().json()
        } catch {
          // Record receipt even when the body is malformed or unreadable.
        }
        receiveRefreshDiagnostic(
          logger,
          attemptId,
          response.status,
          body,
          correlation
        )
      }

      if (!response.ok) {
        logger.errorRaw('api.error', {
          method,
          url: normalizedUrl,
          status: response.status,
          statusText: response.statusText,
          durationMs,
          ...correlation
        })
      } else if (shouldLogSuccess(url, prefixes)) {
        logger.trackRaw(
          'api.success',
          {
            method,
            url: normalizedUrl,
            status: response.status,
            durationMs,
            ...correlation
          },
          'action',
          'info'
        )
      } else if (durationMs >= slowThreshold) {
        logger.warn('api.slow_request', {
          method,
          url: normalizedUrl,
          status: response.status,
          durationMs,
          ...correlation
        })
      }
      return response
    } catch (err) {
      const durationMs = Math.round(performance.now() - start)
      const errorName = err instanceof Error ? err.name : undefined
      if (attemptId) {
        logger.error('auth.token_refresh_transport_failed', {
          attemptId,
          reason: errorName === 'AbortError' ? 'abort' : 'network_or_cors'
        })
      }
      logger.errorRaw('api.error', {
        method,
        url: normalizeUrl(url),
        message: err instanceof Error ? err.message : String(err),
        failureKind:
          errorName === 'AbortError'
            ? 'abort'
            : navigator.onLine
              ? 'network_or_cors'
              : 'offline',
        durationMs
      })
      throw err
    }
  }

  return () => {
    window.fetch = origFetch
  }
}

type TrackedXHR = XMLHttpRequest & {
  __loggerMethod?: string
  __loggerUrl?: string
  __loggerStart?: number
}

function patchXHR(
  logger: LoggerService,
  options: NetworkInterceptorOptions
): () => void {
  const XHRCtor = window.XMLHttpRequest
  const origOpen = XHRCtor.prototype.open
  const origSend = XHRCtor.prototype.send
  const prefixes = options.logSuccessfulUrlPrefixes ?? []
  const excludedPrefixes = options.excludedUrlPrefixes ?? []
  const slowThreshold = options.slowRequestThresholdMs ?? 5_000

  XHRCtor.prototype.open = function (
    this: TrackedXHR,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__loggerMethod = method?.toUpperCase()
    this.__loggerUrl = url.toString()
    return (origOpen as (...a: unknown[]) => unknown).apply(this, [
      method,
      url,
      ...rest
    ])
  } as typeof XHRCtor.prototype.open

  XHRCtor.prototype.send = function (this: TrackedXHR, ...args: unknown[]) {
    const url = this.__loggerUrl
    if (isStaticAssetUrl(url) || shouldExclude(url, excludedPrefixes)) {
      return (origSend as (...a: unknown[]) => unknown).apply(this, args)
    }

    this.__loggerStart = performance.now()
    const attemptId = isAuthRefreshRequest(url, this.__loggerMethod)
      ? startRefreshDiagnostic(logger, 'xhr', args[0])
      : undefined
    let responseRecorded = false
    // `load` precedes Axios's `loadend` response handling, so response
    // fingerprints are available when the application verifies persistence.
    const onLoad = () => {
      if (!attemptId || responseRecorded || this.status === 0) return
      responseRecorded = true
      let body: unknown
      let correlation: { requestId?: string; traceId?: string } = {}
      try {
        body = this.responseType === 'json' ? this.response : this.responseText
        correlation = {
          requestId:
            this.getResponseHeader('x-request-id') ??
            this.getResponseHeader('request-id') ??
            undefined,
          traceId:
            this.getResponseHeader('traceparent') ??
            this.getResponseHeader('x-trace-id') ??
            undefined
        }
      } catch {
        // Unsupported response types still get a receipt/status event.
      }
      receiveRefreshDiagnostic(
        logger,
        attemptId,
        this.status,
        body,
        correlation
      )
    }
    let failureKind:
      | 'abort'
      | 'timeout'
      | 'network_or_cors'
      | 'offline'
      | undefined
    const onAbort = () => {
      failureKind = 'abort'
    }
    const onTimeout = () => {
      failureKind = 'timeout'
    }
    const onError = () => {
      failureKind = navigator.onLine ? 'network_or_cors' : 'offline'
    }
    const onLoadEnd = () => {
      onLoad()
      this.removeEventListener('load', onLoad)
      this.removeEventListener('loadend', onLoadEnd)
      this.removeEventListener('abort', onAbort)
      this.removeEventListener('timeout', onTimeout)
      this.removeEventListener('error', onError)
      const durationMs =
        this.__loggerStart != null
          ? Math.round(performance.now() - this.__loggerStart)
          : undefined
      const method = this.__loggerMethod
      const normalizedUrl = normalizeUrl(url)

      if (this.status === 0) {
        if (attemptId)
          logger.error('auth.token_refresh_transport_failed', {
            attemptId,
            reason: failureKind ?? 'network_or_cors'
          })
        logger.errorRaw('api.error', {
          method,
          url: normalizedUrl,
          message:
            failureKind ??
            (navigator.onLine ? 'Network or CORS error' : 'Offline'),
          failureKind:
            failureKind ?? (navigator.onLine ? 'network_or_cors' : 'offline'),
          durationMs
        })
      } else if (this.status >= 400) {
        logger.errorRaw('api.error', {
          method,
          url: normalizedUrl,
          status: this.status,
          statusText: this.statusText,
          durationMs
        })
      } else if (shouldLogSuccess(url, prefixes)) {
        logger.trackRaw(
          'api.success',
          { method, url: normalizedUrl, status: this.status, durationMs },
          'action',
          'info'
        )
      } else if (durationMs != null && durationMs >= slowThreshold) {
        logger.warn('api.slow_request', {
          method,
          url: normalizedUrl,
          status: this.status,
          durationMs
        })
      }
    }
    this.addEventListener('abort', onAbort)
    this.addEventListener('load', onLoad)
    this.addEventListener('timeout', onTimeout)
    this.addEventListener('error', onError)
    this.addEventListener('loadend', onLoadEnd)

    try {
      return (origSend as (...a: unknown[]) => unknown).apply(this, args)
    } catch (error) {
      this.removeEventListener('load', onLoad)
      this.removeEventListener('loadend', onLoadEnd)
      this.removeEventListener('abort', onAbort)
      this.removeEventListener('timeout', onTimeout)
      this.removeEventListener('error', onError)
      if (attemptId)
        logger.error('auth.token_refresh_transport_failed', {
          attemptId,
          reason: 'request_setup_failed'
        })
      logger.exception(
        'api.request_setup_failed',
        error,
        { method: this.__loggerMethod, url: normalizeUrl(url) },
        { category: 'api' }
      )
      throw error
    }
  } as typeof XHRCtor.prototype.send

  return () => {
    XHRCtor.prototype.open = origOpen
    XHRCtor.prototype.send = origSend
  }
}

// Patches window.fetch and XMLHttpRequest globally so every network call —
// axios (which sits on top of one of these two), raw fetch, raw XHR, or a
// third-party SDK's own client — gets logged the same way, without needing
// to be manually wired per instance.
export function mountNetworkInterceptor(
  loggerOrOptions: LoggerService | NetworkInterceptorOptions = {},
  suppliedOptions: NetworkInterceptorOptions = {}
): () => void {
  const logger = 'trackRaw' in loggerOrOptions ? loggerOrOptions : defaultLogger
  const options =
    'trackRaw' in loggerOrOptions ? suppliedOptions : loggerOrOptions
  const unpatchFetch = patchFetch(logger, options)
  const unpatchXHR = patchXHR(logger, options)
  return () => {
    unpatchFetch()
    unpatchXHR()
  }
}
